/* ADCELL assigns `pid` (partner programme) and `eventid` (sale event); they must not be changed. */
const ADCELL_PID = '18893'
const ADCELL_SALE_EVENT_ID = '24317'
const ADCELL_TRACKER_URL = 'https://t.adcell.com/t/track.js'

const TRACKING_PARAMS_STORAGE_KEY = 'adcell-tracking-params'

// ADCELL keeps its own first-party cookie for 30 days, so parked parameters expire with it.
const TRACKING_PARAMS_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

export interface AdcellTrackingParams {
  bid: string
  adcref: string
}

interface ParkedAdcellTrackingParams extends AdcellTrackingParams {
  parkedAt: number
}

export interface AdcellPaidSubscription {
  /* Identifies the transaction in ADCELL's commission reporting. Must never carry personal data. */
  reference: string
  /* Net amount, after any discount and excluding VAT. */
  netAmount: number
  vouchers: string[]
}

const readRawParam = (search: string, name: string) => new RegExp(`[?&]${name}=([^&]*)`).exec(search)?.[1] ?? ''

const decodeParam = (value: string) => {
  try {
    return decodeURIComponent(value)
  } catch {
    /* A malformed escape sequence must not take the plugin down; hand ADCELL what we got. */
    return value
  }
}

/*
 * ADCELL decodes `bid` itself, so pass it on raw rather than decoding it twice. It decodes
 * `adcref` only when reading it from the URL, so that one has to reach it already decoded.
 */
export const readAdcellTrackingParams = (search: string): AdcellTrackingParams | null => {
  const bid = readRawParam(search, 'bid')
  if (!bid) return null

  return { bid, adcref: decodeParam(readRawParam(search, 'adcref')) }
}

export const parkAdcellTrackingParams = (params: AdcellTrackingParams) => {
  const parked: ParkedAdcellTrackingParams = { ...params, parkedAt: Date.now() }

  try {
    localStorage.setItem(TRACKING_PARAMS_STORAGE_KEY, JSON.stringify(parked))
  } catch {
    /* Without storage the visit simply goes unattributed. */
  }
}

export const clearParkedAdcellTrackingParams = () => {
  try {
    localStorage.removeItem(TRACKING_PARAMS_STORAGE_KEY)
  } catch {
    /* Nothing to clean up if storage is unavailable. */
  }
}

export const readParkedAdcellTrackingParams = (): AdcellTrackingParams | null => {
  try {
    const parked = localStorage.getItem(TRACKING_PARAMS_STORAGE_KEY)
    if (!parked) return null

    const { bid, adcref, parkedAt } = JSON.parse(parked) as ParkedAdcellTrackingParams
    if (!bid || !parkedAt || Date.now() - parkedAt > TRACKING_PARAMS_MAX_AGE_MS) {
      localStorage.removeItem(TRACKING_PARAMS_STORAGE_KEY)
      return null
    }

    return { bid, adcref }
  } catch {
    return null
  }
}

export const buildAdcellPurchaseReference = (businessId: string, purchasedAt: Date) =>
  `${businessId}-${Math.floor(purchasedAt.getTime() / 1000)}`

export const buildAdcellConversionUrl = ({ reference, netAmount, vouchers }: AdcellPaidSubscription) => {
  const params = new URLSearchParams({
    pid: ADCELL_PID,
    eventid: ADCELL_SALE_EVENT_ID,
    referenz: reference,
    betrag: netAmount.toFixed(2),
  })

  vouchers.forEach(voucher => params.append('vouchers[]', voucher))

  return `${ADCELL_TRACKER_URL}?${params.toString()}`
}
